import {
  MetlifeApplication,
  MetlifeApplicationType,
  MetlifeGender,
  MetlifeStatus,
} from '../types/metlife.types';

/** Labels are literal in the legacy app (they are names, not translated UILM text). */
export const METLIFE_APPLICATION_TYPES: { value: MetlifeApplicationType; label: string }[] = [
  { value: 'Employee', label: 'Employee' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Child1', label: 'Child-1' },
  { value: 'Child2', label: 'Child-2' },
  { value: 'Child3', label: 'Child-3' },
];

/** Existing UILM keys for the gender options the legacy form offered. */
export const METLIFE_GENDERS: { value: MetlifeGender; labelKey: string }[] = [
  { value: 'MALE', labelKey: 'MALE' },
  { value: 'FEMALE', labelKey: 'FEMALE' },
];

/** Every stored gender (imported records can hold OTHER or NA, which the form no longer offers). */
const METLIFE_GENDER_LABEL_KEYS: Record<MetlifeGender, string> = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  OTHER: 'GENDER_OTHER',
  NA: 'GENDER_NOT_APPLICABLE',
};

export const genderLabelKey = (gender?: MetlifeGender | null) =>
  gender ? (METLIFE_GENDER_LABEL_KEYS[gender] ?? gender) : '';

/** Statuses an administrator can pick from the inline selector; Completed also asks for the policy id. */
export const METLIFE_SELECTABLE_STATUSES: { value: MetlifeStatus; labelKey: string }[] = [
  { value: 'Pending', labelKey: 'PENDING' },
  { value: 'InProgress', labelKey: 'IN_PROGRESS' },
  { value: 'Completed', labelKey: 'COMPLETED' },
];

/** Every status, for the requests filter. */
export const METLIFE_FILTER_STATUSES: { value: MetlifeStatus; labelKey: string }[] = [
  { value: 'Pending', labelKey: 'PENDING' },
  { value: 'InProgress', labelKey: 'IN_PROGRESS' },
  { value: 'Completed', labelKey: 'COMPLETED' },
  { value: 'Canceled', labelKey: 'CANCELED' },
  { value: 'Excluded', labelKey: 'EXCLUDED' },
  { value: 'Archived', labelKey: 'ARCHIVED' },
];

export const typeLabel = (type: MetlifeApplicationType) =>
  METLIFE_APPLICATION_TYPES.find((option) => option.value === type)?.label ?? type;

/**
 * An application holds its policy slot until it is archived or cancelled -- an employee may have
 * only one live application per type (Employee, Spouse, Child-1, ...).
 */
export const holdsTypeSlot = (status: MetlifeStatus) =>
  status !== 'Archived' && status !== 'Canceled';

/** The types the applicant can no longer choose because a live application already covers them. */
export const takenTypes = (
  applications: Pick<MetlifeApplication, 'Status' | 'TypeOfApplication'>[],
  excludeType?: MetlifeApplicationType
) =>
  new Set(
    applications
      .filter((item) => holdsTypeSlot(item.Status) && item.TypeOfApplication !== excludeType)
      .map((item) => item.TypeOfApplication)
  );

/**
 * The maternity-benefit question applies to a female policy holder who is the employee or the
 * spouse (legacy `METLIFE_MATERNITY_ELIGIBLE_TYPES`, CreateMetLifeApplicationCommandValidator).
 */
export const asksMaternityBenefit = (
  type: MetlifeApplicationType | '',
  gender: MetlifeGender | ''
) => (type === 'Employee' || type === 'Spouse') && gender === 'FEMALE';

/** A dependant is recorded against the employee, so their "designation" is derived from the name. */
export const dependantDesignation = (fullName: string) => `Dependance of ${fullName}`.trim();

/** The employee's own policy has no separate id, so the legacy form fixed it to `N/A`. */
export const NO_METLIFE_ID = 'N/A';

/** Legacy UI: applicants edit or withdraw only while pending. */
export const canEditMetlife = (application: MetlifeApplication) => application.Status === 'Pending';

/** Legacy `CancelMetLifeApplicationAsync`: only a completed *spouse* policy can be cancelled. */
export const canCancelMetlife = (application: MetlifeApplication) =>
  application.TypeOfApplication === 'Spouse' && application.Status === 'Completed';

/** Reviewers can archive an application that has not been completed. */
export const canArchiveMetlife = (application: MetlifeApplication) =>
  application.Status === 'Pending' || application.Status === 'InProgress';

/** Only a completed application can be excluded from the policy. */
export const canExcludeMetlife = (application: MetlifeApplication) =>
  application.Status === 'Completed';

/** Legacy UI: a reviewer may edit any application except an archived one. */
export const canReviewerEditMetlife = (application: MetlifeApplication) =>
  application.Status !== 'Archived';

/** Statuses outside the inline selector cannot be moved by it (archived, cancelled, excluded). */
export const isMetlifeStatusLocked = (application: MetlifeApplication) =>
  !METLIFE_SELECTABLE_STATUSES.some((option) => option.value === application.Status);
